import React from 'react';
import { useTranslation } from 'react-i18next';
import { US_STATES, SPECIALIZATIONS, LANGUAGES } from '@immigration-helper/shared';

interface Filters {
  state: string;
  specialization: string;
  language: string;
  freeOnly: boolean;
}

interface SearchFiltersProps {
  filters: Filters;
  onChange: (filters: Filters) => void;
  onClear: () => void;
}

export default function SearchFilters({ filters, onChange, onClear }: SearchFiltersProps) {
  const { t } = useTranslation();

  const hasActiveFilters = filters.state || filters.specialization || filters.language || filters.freeOnly;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-gray-900">{t('common.filter')}</h3>
        {hasActiveFilters && (
          <button
            onClick={onClear}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            {t('common.clearAll')}
          </button>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* State Filter */}
        <div>
          <label className="label">{t('resources.filters.state')}</label>
          <select
            value={filters.state}
            onChange={(e) => onChange({ ...filters, state: e.target.value })}
            className="input"
          >
            <option value="">All states</option>
            {US_STATES.map(state => (
              <option key={state.code} value={state.code}>
                {state.name}
              </option>
            ))}
          </select>
        </div>

        {/* Specialization Filter */}
        <div>
          <label className="label">{t('resources.filters.specialization')}</label>
          <select
            value={filters.specialization}
            onChange={(e) => onChange({ ...filters, specialization: e.target.value })}
            className="input"
          >
            <option value="">All specializations</option>
            {SPECIALIZATIONS.map(spec => (
              <option key={spec} value={spec}>
                {spec}
              </option>
            ))}
          </select>
        </div>

        {/* Language Filter */}
        <div>
          <label className="label">{t('resources.filters.language')}</label>
          <select
            value={filters.language}
            onChange={(e) => onChange({ ...filters, language: e.target.value })}
            className="input"
          >
            <option value="">All languages</option>
            {LANGUAGES.map(lang => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
        </div>

        {/* Free Only Toggle */}
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-lg w-full">
            <input
              type="checkbox"
              checked={filters.freeOnly}
              onChange={(e) => onChange({ ...filters, freeOnly: e.target.checked })}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">
              {t('resources.filters.freeOnly')}
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}
